import type { BannerTexts, ConsentBasis, ConsentCategory } from "./types";

/**
 * Built-in visitor-facing texts (nl/en/de), written to the AP rules in
 * docs/privacy-seo-research.md A2: say who, why, how many third parties and
 * how to withdraw, with "accept"/"reject" wording. Placeholders: {controller},
 * {purposes}, {count}. Owners may override the banner texts per locale.
 */

export const CONSENT_TEXT_LOCALES = ["nl", "en", "de"] as const;

const nl = {
  title: "Jouw privacy, jouw keuze",
  body: "{controller} gebruikt cookies en vergelijkbare technieken. Noodzakelijke cookies zorgen dat de website werkt. Met jouw toestemming gebruiken we ze ook voor {purposes}. Daarbij delen we gegevens met {count} externe partijen. Je kunt je keuze altijd wijzigen of intrekken via de knop Cookie-instellingen op elke pagina.",
  bodyNoThirdParties: "{controller} gebruikt cookies en vergelijkbare technieken. Noodzakelijke cookies zorgen dat de website werkt. Met jouw toestemming gebruiken we ze ook voor {purposes}. Je kunt je keuze altijd wijzigen of intrekken via de knop Cookie-instellingen op elke pagina.",
  acceptAll: "Alles accepteren",
  rejectAll: "Alles weigeren",
  preferences: "Voorkeuren aanpassen",
  save: "Keuze opslaan",
  floatingLabel: "Cookie-instellingen",
  dialogTitle: "Cookievoorkeuren",
  dialogIntro: "Kies per doel welke cookies je toestaat. Je kunt je keuze later altijd wijzigen of intrekken.",
  close: "Sluiten",
  policyLink: "Privacy- en cookieverklaring",
  alwaysOn: "Altijd aan",
  objectLabel: "Bezwaar maken",
  services: "Diensten en cookies",
  provider: "Aanbieder",
  retention: "Bewaartermijn",
  thirdParty: "cookie van {domain}",
  gpcNotice: "Je browser vraagt om niet gevolgd te worden. We hebben daarom alle optionele cookies geweigerd.",
  gpcChange: "Wijzigen",
  embedTitle: "Inhoud van {service}",
  embedBody: "Deze inhoud wordt geladen vanaf {provider}. Daarbij kunnen cookies worden geplaatst en gegevens worden gedeeld.",
  embedLoad: "Laden en accepteren",
  embedAlways: "Altijd toestaan voor {service}",
  consentId: "Toestemmings-ID",
  consentDate: "Gegeven op",
  noChoice: "Je hebt nog geen keuze gemaakt.",
  saved: "Je voorkeuren zijn opgeslagen.",
  "purpose.functional": "extra functies zoals video's en kaarten",
  "purpose.analytics": "statistieken",
  "purpose.marketing": "marketing en advertenties",
  "category.necessary": "Noodzakelijk",
  "category.functional": "Functioneel",
  "category.analytics": "Statistieken",
  "category.marketing": "Marketing",
  "categoryHelp.necessary": "Nodig om de website te laten werken, bijvoorbeeld om je keuze over cookies te onthouden. Deze kun je niet uitzetten.",
  "categoryHelp.functional": "Extra functies, zoals ingesloten video's en kaarten.",
  "categoryHelp.analytics": "Inzicht in hoe de website wordt gebruikt, zodat we hem kunnen verbeteren.",
  "categoryHelp.marketing": "Advertenties meten en tonen, ook op andere websites.",
  "basis.exempt": "Geen toestemming nodig",
  "basis.legitimate_interest": "Gerechtvaardigd belang — je kunt bezwaar maken",
  "basis.consent": "Alleen met jouw toestemming",
};

const en: typeof nl = {
  title: "Your privacy, your choice",
  body: "{controller} uses cookies and similar technologies. Necessary cookies make the website work. With your consent we also use them for {purposes}. This shares data with {count} third parties. You can change or withdraw your choice at any time with the Cookie settings button on every page.",
  bodyNoThirdParties: "{controller} uses cookies and similar technologies. Necessary cookies make the website work. With your consent we also use them for {purposes}. You can change or withdraw your choice at any time with the Cookie settings button on every page.",
  acceptAll: "Accept all",
  rejectAll: "Reject all",
  preferences: "Customise",
  save: "Save choices",
  floatingLabel: "Cookie settings",
  dialogTitle: "Cookie preferences",
  dialogIntro: "Choose per purpose which cookies you allow. You can change or withdraw your choice at any time.",
  close: "Close",
  policyLink: "Privacy and cookie statement",
  alwaysOn: "Always on",
  objectLabel: "Object",
  services: "Services and cookies",
  provider: "Provider",
  retention: "Retention",
  thirdParty: "cookie from {domain}",
  gpcNotice: "Your browser asks not to be tracked, so we rejected all optional cookies.",
  gpcChange: "Change",
  embedTitle: "Content from {service}",
  embedBody: "This content is loaded from {provider}, which may set cookies and receive data.",
  embedLoad: "Load and accept",
  embedAlways: "Always allow {service}",
  consentId: "Consent ID",
  consentDate: "Given on",
  noChoice: "You haven't made a choice yet.",
  saved: "Your preferences have been saved.",
  "purpose.functional": "extra features such as videos and maps",
  "purpose.analytics": "statistics",
  "purpose.marketing": "marketing and advertising",
  "category.necessary": "Necessary",
  "category.functional": "Functional",
  "category.analytics": "Statistics",
  "category.marketing": "Marketing",
  "categoryHelp.necessary": "Required for the website to work, for example to remember your cookie choice. These can't be switched off.",
  "categoryHelp.functional": "Extra features, such as embedded videos and maps.",
  "categoryHelp.analytics": "Insight into how the website is used, so we can improve it.",
  "categoryHelp.marketing": "Measuring and showing ads, also on other websites.",
  "basis.exempt": "No consent required",
  "basis.legitimate_interest": "Legitimate interest — you can object",
  "basis.consent": "Only with your consent",
};

const de: typeof nl = {
  title: "Ihre Privatsphäre, Ihre Wahl",
  body: "{controller} verwendet Cookies und ähnliche Technologien. Notwendige Cookies sorgen dafür, dass die Website funktioniert. Mit Ihrer Einwilligung nutzen wir sie auch für {purposes}. Dabei werden Daten mit {count} Drittanbietern geteilt. Sie können Ihre Wahl jederzeit über die Schaltfläche Cookie-Einstellungen auf jeder Seite ändern oder widerrufen.",
  bodyNoThirdParties: "{controller} verwendet Cookies und ähnliche Technologien. Notwendige Cookies sorgen dafür, dass die Website funktioniert. Mit Ihrer Einwilligung nutzen wir sie auch für {purposes}. Sie können Ihre Wahl jederzeit über die Schaltfläche Cookie-Einstellungen auf jeder Seite ändern oder widerrufen.",
  acceptAll: "Alle akzeptieren",
  rejectAll: "Alle ablehnen",
  preferences: "Einstellungen anpassen",
  save: "Auswahl speichern",
  floatingLabel: "Cookie-Einstellungen",
  dialogTitle: "Cookie-Einstellungen",
  dialogIntro: "Wählen Sie für jeden Zweck, welche Cookies Sie zulassen. Sie können Ihre Wahl jederzeit ändern oder widerrufen.",
  close: "Schließen",
  policyLink: "Datenschutz- und Cookie-Erklärung",
  alwaysOn: "Immer aktiv",
  objectLabel: "Widersprechen",
  services: "Dienste und Cookies",
  provider: "Anbieter",
  retention: "Speicherdauer",
  thirdParty: "Cookie von {domain}",
  gpcNotice: "Ihr Browser bittet darum, nicht verfolgt zu werden. Daher haben wir alle optionalen Cookies abgelehnt.",
  gpcChange: "Ändern",
  embedTitle: "Inhalt von {service}",
  embedBody: "Dieser Inhalt wird von {provider} geladen. Dabei können Cookies gesetzt und Daten übermittelt werden.",
  embedLoad: "Laden und akzeptieren",
  embedAlways: "{service} immer erlauben",
  consentId: "Einwilligungs-ID",
  consentDate: "Erteilt am",
  noChoice: "Sie haben noch keine Wahl getroffen.",
  saved: "Ihre Einstellungen wurden gespeichert.",
  "purpose.functional": "Zusatzfunktionen wie Videos und Karten",
  "purpose.analytics": "Statistiken",
  "purpose.marketing": "Marketing und Werbung",
  "category.necessary": "Notwendig",
  "category.functional": "Funktional",
  "category.analytics": "Statistiken",
  "category.marketing": "Marketing",
  "categoryHelp.necessary": "Erforderlich, damit die Website funktioniert, z. B. um Ihre Cookie-Auswahl zu speichern. Diese können nicht deaktiviert werden.",
  "categoryHelp.functional": "Zusatzfunktionen wie eingebettete Videos und Karten.",
  "categoryHelp.analytics": "Einblick in die Nutzung der Website, damit wir sie verbessern können.",
  "categoryHelp.marketing": "Anzeigen messen und zeigen, auch auf anderen Websites.",
  "basis.exempt": "Keine Einwilligung erforderlich",
  "basis.legitimate_interest": "Berechtigtes Interesse — Sie können widersprechen",
  "basis.consent": "Nur mit Ihrer Einwilligung",
};

export const DEFAULT_CONSENT_TEXTS: Record<string, typeof nl> = { nl, en, de };
export type ConsentTextKey = keyof typeof nl;

/** Default texts for a locale; locales without built-in texts fall back to English, never to a mix. */
export function defaultConsentTexts(locale: string): typeof nl {
  return DEFAULT_CONSENT_TEXTS[locale.slice(0, 2)] || en;
}

export function mergeBannerTexts(locale: string, overrides: BannerTexts | undefined): typeof nl {
  const base = defaultConsentTexts(locale);
  const merged = { ...base };
  for (const [key, value] of Object.entries(overrides || {})) if (typeof value === "string" && value.trim()) (merged as Record<string, string>)[key] = value.trim();
  return merged;
}

export const categoryLabelKey = (category: ConsentCategory) => `category.${category}` as ConsentTextKey;
export const basisLabelKey = (basis: ConsentBasis) => `basis.${basis}` as ConsentTextKey;
